import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Calendar, BookOpen, Brain, Star, FileText, AlertTriangle, Heart, CheckCircle, XCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { differenceInYears, differenceInMonths, parseISO, format } from "date-fns";

interface ChildProfileDialogProps {
  child: any;
  photoUrl?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formatAge = (dob: string | null) => {
  if (!dob) return "-";
  const birth = parseISO(dob);
  const years = differenceInYears(new Date(), birth);
  if (years >= 1) return `${years} سنة`;
  const months = differenceInMonths(new Date(), birth);
  return `${months} شهر`;
};

const ChildProfileDialog = ({ child, photoUrl, open, onOpenChange }: ChildProfileDialogProps) => {
  const [loading, setLoading] = useState(true);
  const [attendance, setAttendance] = useState<any[]>([]);
  const [observations, setObservations] = useState<any[]>([]);
  const [examResults, setExamResults] = useState<any[]>([]);
  const [activities, setActivities] = useState<any[]>([]);
  const [weeklyReport, setWeeklyReport] = useState<any | null>(null);

  useEffect(() => {
    if (!open || !child?.id) return;
    const fetchProfile = async () => {
      setLoading(true);
      const [attRes, obsRes, examRes, actRes, reportRes] = await Promise.all([
        supabase
          .from("attendance")
          .select("*")
          .eq("attendee_id", child.id)
          .eq("attendee_type", "child")
          .order("date", { ascending: false })
          .limit(10),
        supabase
          .from("child_observations")
          .select("*")
          .eq("child_id", child.id)
          .order("observation_date", { ascending: false })
          .limit(5),
        supabase
          .from("exam_submissions")
          .select("*, exams(title)")
          .eq("child_id", child.id)
          .order("created_at", { ascending: false })
          .limit(5),
        supabase
          .from("child_activities")
          .select("*")
          .eq("child_id", child.id)
          .order("completed_at", { ascending: false })
          .limit(5),
        supabase
          .from("child_weekly_reports")
          .select("*")
          .eq("child_id", child.id)
          .order("week_end", { ascending: false })
          .limit(1),
      ]);
      setAttendance(attRes.data || []);
      setObservations(obsRes.data || []);
      setExamResults(examRes.data || []);
      setActivities(actRes.data || []);
      setWeeklyReport(reportRes.data?.[0] || null);
      setLoading(false);
    };
    fetchProfile();
  }, [open, child?.id]);

  if (!child) return null;

  const sentimentMap: Record<string, { label: string; color: string }> = {
    positive: { label: "إيجابي", color: "text-emerald-600" },
    negative: { label: "سلبي", color: "text-destructive" },
    neutral: { label: "محايد", color: "text-muted-foreground" },
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>ملف الطفل</DialogTitle>
        </DialogHeader>

        {/* Header */}
        <div className="flex items-center gap-4 pb-4 border-b">
          <Avatar className="w-16 h-16">
            <AvatarImage src={photoUrl || ""} />
            <AvatarFallback className="bg-primary/10 text-primary text-xl">
              {child.full_name?.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-foreground">{child.full_name}</h3>
            <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
              <span>العمر: {formatAge(child.date_of_birth)}</span>
              {child.date_of_birth && <span>• {child.date_of_birth}</span>}
            </div>
            <div className="flex items-center gap-2 mt-1">
              {child.classes?.name && <Badge variant="outline">{child.classes.name}</Badge>}
              {child.health_notes ? (
                <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50">
                  <AlertTriangle className="w-3 h-3 ml-1" />ملاحظات صحية
                </Badge>
              ) : (
                <Badge variant="outline" className="text-emerald-600 border-emerald-200 bg-emerald-50">
                  <Heart className="w-3 h-3 ml-1" />سليم
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Parent info */}
        {(child.parent_name || child.parent_phone) && (
          <div className="text-sm text-muted-foreground flex gap-4">
            {child.parent_name && <span>ولي الأمر: {child.parent_name}</span>}
            {child.parent_phone && <span dir="ltr">📞 {child.parent_phone}</span>}
          </div>
        )}

        {child.health_notes && (
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
            <strong>ملاحظات صحية:</strong> {child.health_notes}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : (
          <Tabs defaultValue="attendance" dir="rtl">
            <TabsList className="grid grid-cols-5 w-full">
              <TabsTrigger value="attendance" className="text-xs">الحضور</TabsTrigger>
              <TabsTrigger value="observations" className="text-xs">الملاحظات</TabsTrigger>
              <TabsTrigger value="exams" className="text-xs">الامتحانات</TabsTrigger>
              <TabsTrigger value="activities" className="text-xs">الأنشطة</TabsTrigger>
              <TabsTrigger value="report" className="text-xs">التقرير</TabsTrigger>
            </TabsList>

            <TabsContent value="attendance" className="space-y-2 mt-3">
              {attendance.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">لا توجد سجلات حضور</p>
              ) : (
                attendance.map((a) => (
                  <div key={a.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/50 text-sm">
                    <div className="flex items-center gap-2">
                      {a.status === "present" ? (
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <XCircle className="w-4 h-4 text-destructive" />
                      )}
                      <span>{a.status === "present" ? "حاضر" : a.status === "absent" ? "غائب" : a.status}</span>
                    </div>
                    <span className="text-muted-foreground">{a.date}</span>
                  </div>
                ))
              )}
            </TabsContent>

            <TabsContent value="observations" className="space-y-2 mt-3">
              {observations.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">لا توجد ملاحظات</p>
              ) : (
                observations.map((o) => (
                  <Card key={o.id} className="shadow-none">
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between mb-1">
                        <Badge variant="outline" className="text-xs">{o.category}</Badge>
                        <span className={`text-xs ${sentimentMap[o.sentiment]?.color || ""}`}>
                          {sentimentMap[o.sentiment]?.label || o.sentiment}
                        </span>
                      </div>
                      {o.notes && <p className="text-sm text-foreground">{o.notes}</p>}
                      <p className="text-xs text-muted-foreground mt-1">{o.observation_date}</p>
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>

            <TabsContent value="exams" className="space-y-2 mt-3">
              {examResults.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">لا توجد نتائج امتحانات</p>
              ) : (
                examResults.map((e) => (
                  <div key={e.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/50 text-sm">
                    <div>
                      <span className="font-medium">{e.exams?.title || "امتحان"}</span>
                      <span className="text-muted-foreground mr-2">
                        {e.total_score !== null && e.max_score ? `${e.total_score}/${e.max_score}` : e.status}
                      </span>
                    </div>
                    {e.percentage !== null && (
                      <Badge variant={e.percentage >= 70 ? "default" : "destructive"} className="text-xs">
                        {Math.round(e.percentage)}%
                      </Badge>
                    )}
                  </div>
                ))
              )}
            </TabsContent>

            <TabsContent value="activities" className="space-y-2 mt-3">
              {activities.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">لا توجد أنشطة</p>
              ) : (
                activities.map((a) => (
                  <div key={a.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/50 text-sm">
                    <div>
                      <span className="font-medium">{a.activity_name}</span>
                      <span className="text-muted-foreground mr-2">{a.activity_type}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs">{a.score} نقطة</span>
                      <div className="flex">
                        {Array.from({ length: a.stars || 0 }).map((_, i) => (
                          <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />
                        ))}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </TabsContent>

            <TabsContent value="report" className="mt-3">
              {!weeklyReport ? (
                <p className="text-sm text-muted-foreground text-center py-4">لا يوجد تقرير أسبوعي</p>
              ) : (
                <div className="space-y-3">
                  <div className="text-xs text-muted-foreground text-center">
                    {weeklyReport.week_start} → {weeklyReport.week_end}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: "أكاديمي", value: weeklyReport.academic_score },
                      { label: "سلوكي", value: weeklyReport.behavioral_score },
                      { label: "صحي", value: weeklyReport.health_score },
                      { label: "نفسي", value: weeklyReport.psychological_score },
                    ].map((s) => (
                      <div key={s.label} className="p-2 rounded-lg bg-muted/50 text-center">
                        <div className="text-lg font-bold text-foreground">{s.value}/5</div>
                        <div className="text-xs text-muted-foreground">{s.label}</div>
                      </div>
                    ))}
                  </div>
                  {weeklyReport.ai_suggestions && (
                    <div className="p-3 rounded-lg bg-primary/5 border text-sm">
                      <strong>توصيات:</strong> {weeklyReport.ai_suggestions}
                    </div>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ChildProfileDialog;
